// components/OutboundV12Panel.jsx
// WeConnect CRM — Módulo Outbound V12 — Auditoría + Confirmación de Campaña
// Dry-run: POST /api/outbound/procesar-csv
// Confirmación: POST /api/outbound/v12/confirmar-campana (guarda en Supabase, NO envía)

import React, { useState, useMemo } from 'react';

// Mismo patrón que OutboundPanel v1: authFetch local + API_URL desde env
const API_URL = process.env.REACT_APP_API_URL || '';

function authFetch(url, opts) {
  const token = localStorage.getItem('wc_token');
  return fetch(url, {
    ...opts,
    headers: {
      ...(opts && opts.headers ? opts.headers : {}),
      ...(token ? { Authorization: 'Bearer ' + token } : {})
    }
  });
}

async function safeJson(res, label) {
  const ct = res.headers.get('content-type') || '';
  if (ct.includes('application/json')) {
    return res.json();
  }
  const text = await res.text();
  const preview = text.substring(0, 300);
  console.error(`[safeJson] ${label} → HTTP ${res.status} | Content-Type: ${ct} | Body: ${preview}`);
  throw new Error(`Error ${res.status} en ${label}: respuesta no es JSON (${ct || 'sin content-type'}). Verificar ruta y backend.`);
}

// ═══════════════════════════════════════════════════════
// Configuración de templates (mirror del backend)
// ═══════════════════════════════════════════════════════

const TEMPLATES = [
  { key: 'hito_2_dash', nombre: 'Hito 2 — Factura disponible', grupo: 'Cobranza', requiereCiclo: true, camposManuales: [] },
  { key: 'hito_3_dash', nombre: 'Hito 3 — Recordatorio 1er vto', grupo: 'Cobranza', requiereCiclo: true, camposManuales: [] },
  { key: 'hito_4_dash', nombre: 'Hito 4 — Llegó 1er vto', grupo: 'Cobranza', requiereCiclo: true, camposManuales: [] },
  { key: 'hito_5_dash', nombre: 'Hito 5 — Se acerca 2do vto', grupo: 'Cobranza', requiereCiclo: true, camposManuales: [] },
  { key: 'hito_6_dash', nombre: 'Hito 6 — Llegó 2do vto', grupo: 'Cobranza', requiereCiclo: true, camposManuales: [] },
  { key: 'ciclo_vigente_dash', nombre: 'Ciclo vigente', grupo: 'Cobranza', requiereCiclo: true, camposManuales: [] },
  { key: 'hito_7_dash', nombre: 'Hito 7 — Servicio interrumpido', grupo: 'Corte', requiereCiclo: true, camposManuales: [] },
  { key: 'hito_8_dash', nombre: 'Hito 8 — Oferta reconexión', grupo: 'Recupero', requiereCiclo: false, camposManuales: ['oferta_inicio', 'oferta_maxima'], defaults: { oferta_inicio: '40% de quita', oferta_maxima: '60% de quita' } },
  { key: 'hito_9_dash', nombre: 'Hito 9 — Plan de pago', grupo: 'Recupero', requiereCiclo: false, camposManuales: ['oferta'] },
  { key: 'extra_reconexion_dash', nombre: 'Extra reconexión', grupo: 'Recupero', requiereCiclo: false, camposManuales: ['oferta'] },
  { key: 'extra_volver_dash', nombre: 'Extra volver', grupo: 'Recupero', requiereCiclo: false, camposManuales: ['asesor', 'ciudad'] },
  { key: 'hito_10_dash', nombre: 'Hito 10 — Baja + retiro', grupo: 'Baja / Retiro', requiereCiclo: false, camposManuales: [] },
  { key: 'wehub_activos_tv_dash', nombre: 'WeHUB — TV clientes activos', grupo: 'Comercialización', requiereCiclo: false, camposManuales: [] },
  { key: 'extra_volver_wehub_dash', nombre: 'Extra Volver + WeHUB', grupo: 'Comercialización', requiereCiclo: false, camposManuales: ['asesor', 'ciudad'] },
  { key: 'tareas_mantenimiento_dash', nombre: 'Mantenimiento', grupo: 'Operativo', requiereCiclo: false, camposManuales: ['motivo'] },
  { key: 'corte_electrico_dash', nombre: 'Corte eléctrico', grupo: 'Operativo', requiereCiclo: false, camposManuales: [] },
  { key: 'aviso_general_dash', nombre: 'Aviso general', grupo: 'Operativo', requiereCiclo: false, camposManuales: ['detalle'] },
];

const CICLOS = [
  { key: 'ciclo_1', label: 'Ciclo 1 — 1er: 05 | 2do: 10 | Susp: 11' },
  { key: 'regular', label: 'Regular — 1er: 10 | 2do: 15 | Susp: 16' },
  { key: 'ciclo_2', label: 'Ciclo 2 — 1er: 15 | 2do: 20 | Susp: 21' },
  { key: 'ciclo_3', label: 'Ciclo 3 — 1er: 25 | 2do: 30 | Susp: 01+1' },
];

const CANALES = [
  { key: 'whatsapp', label: 'WhatsApp' },
  { key: 'email', label: 'Email' },
  { key: 'sms', label: 'SMS' },
];

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

const TABS = [
  { key: 'checklist', label: 'Checklist' },
  { key: 'aptos', label: 'Aptos' },
  { key: 'rechazados', label: 'Rechazados' },
  { key: 'invalidos', label: 'Inválidos' },
  { key: 'envios', label: 'Envíos' },
  { key: 'resumen', label: 'Resumen' },
];

const CAMPO_LABELS = { detalle: 'Detalle', oferta: 'Oferta', asesor: 'Asesor', ciudad: 'Ciudad', motivo: 'Motivo', oferta_inicio: 'Oferta inicial %', oferta_maxima: 'Oferta máxima %' };

// ═══════════════════════════════════════════════════════
// CSS inline como string (inyectado una sola vez)
// Permite media queries y pseudo-selectores
// ═══════════════════════════════════════════════════════

const INJECTED_CSS = `
.obv12-panel{width:100%;box-sizing:border-box;padding:24px 28px;color:#e2e0d8;font-family:system-ui,-apple-system,sans-serif;font-size:13px;overflow-y:auto;overflow-x:hidden}
.obv12-panel *{box-sizing:border-box}
.obv12-h1{font-size:20px;font-weight:700;margin:0 0 4px;color:#fff}
.obv12-sub{font-size:12px;color:#8a8880;margin:0 0 20px}
.obv12-card{background:#1a1a22;border-radius:10px;padding:20px;margin-bottom:16px;border:1px solid #2a2a35;width:100%}
.obv12-label{display:block;font-size:11px;font-weight:600;color:#8a8880;text-transform:uppercase;letter-spacing:.5px;margin-bottom:6px}
.obv12-select,.obv12-input{width:100%;padding:9px 12px;background:#12121a;border:1px solid #333;border-radius:6px;color:#e2e0d8;font-size:13px;outline:none;box-sizing:border-box}
.obv12-select:focus,.obv12-input:focus{border-color:#6c5ce7}
.obv12-file{width:100%;padding:10px;background:#12121a;border:1px dashed #444;border-radius:6px;color:#e2e0d8;font-size:12px;cursor:pointer;box-sizing:border-box}
.obv12-file:hover{border-color:#6c5ce7}
.obv12-grid{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-bottom:14px}
.obv12-grid3{display:grid;grid-template-columns:1fr 1fr 1fr;gap:14px;margin-bottom:14px}
@media(max-width:768px){.obv12-grid,.obv12-grid3{grid-template-columns:1fr}}
.obv12-btn{padding:12px 24px;background:#6c5ce7;color:#fff;border:none;border-radius:8px;font-size:14px;font-weight:600;cursor:pointer;width:100%;transition:background .2s}
.obv12-btn:hover{background:#5a4bd1}
.obv12-btn:disabled{background:#333;color:#666;border:1px solid #444;cursor:not-allowed}
.obv12-btn:disabled:hover{background:#333}
.obv12-btn-loading{padding:12px 24px;background:#444;color:#aaa;border:none;border-radius:8px;font-size:14px;font-weight:600;cursor:wait;width:100%}
.obv12-btn-confirm{padding:12px 24px;background:#2a2a35;color:#555;border:1px solid #3a3a45;border-radius:8px;font-size:13px;font-weight:600;cursor:not-allowed;width:100%;margin-top:16px}
.obv12-btn-confirm-active{padding:12px 24px;background:#1a3d2a;color:#00b894;border:1px solid #00b894;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;width:100%;margin-top:16px;transition:background .2s}
.obv12-btn-confirm-active:hover{background:#0d4a2a}
.obv12-stats{display:grid;grid-template-columns:repeat(auto-fit,minmax(90px,1fr));gap:8px;margin-bottom:16px}
.obv12-stat{background:#12121a;border:1px solid #2a2a35;border-radius:8px;padding:12px 8px;text-align:center}
.obv12-stat-val{font-size:22px;font-weight:700;color:#fff}
.obv12-stat-label{font-size:10px;color:#8a8880;text-transform:uppercase;margin-top:3px}
.obv12-tabs{display:flex;gap:0;border-bottom:1px solid #2a2a35;margin-bottom:14px;overflow-x:auto}
.obv12-tab{padding:10px 16px;font-size:12px;font-weight:500;background:none;border:none;color:#8a8880;cursor:pointer;border-bottom:2px solid transparent;transition:all .15s;white-space:nowrap}
.obv12-tab:hover{color:#ccc}
.obv12-tab-active{padding:10px 16px;font-size:12px;font-weight:600;background:none;border:none;color:#6c5ce7;cursor:pointer;border-bottom:2px solid #6c5ce7;white-space:nowrap}
.obv12-table-wrap{max-height:420px;overflow-y:auto;overflow-x:auto;border-radius:6px;border:1px solid #2a2a35;width:100%}
.obv12-table{width:100%;border-collapse:collapse;font-size:12px;min-width:600px}
.obv12-th{text-align:left;padding:8px 10px;background:#12121a;color:#8a8880;font-weight:600;font-size:10px;text-transform:uppercase;border-bottom:1px solid #2a2a35;position:sticky;top:0;z-index:1}
.obv12-td{padding:7px 10px;border-bottom:1px solid #1e1e28;color:#ccc;vertical-align:top}
.obv12-badge{display:inline-block;padding:2px 8px;border-radius:10px;font-size:10px;font-weight:600}
.obv12-chk{display:inline-block;padding:3px 10px;border-radius:4px;font-size:10px;font-weight:700}
.obv12-blocked{background:#3d1f1f;border:1px solid #e17055;border-radius:8px;padding:14px 18px;color:#e17055;font-weight:600;margin-bottom:16px;font-size:14px}
.obv12-success{background:#1f3d2a;border:1px solid #00b894;border-radius:8px;padding:14px 18px;color:#00b894;font-weight:600;margin-bottom:16px;font-size:14px}
.obv12-error{background:#3d1f1f;border:1px solid #e17055;border-radius:8px;padding:12px 16px;color:#e17055;font-size:12px;margin-bottom:14px;white-space:pre-wrap}
.obv12-warning{background:#3d3520;border:1px solid #fdcb6e;border-radius:8px;padding:10px 14px;color:#fdcb6e;font-size:12px;margin-bottom:8px}
.obv12-mono{font-family:'SF Mono',Monaco,Consolas,monospace;font-size:11px}
.obv12-ok{color:#00b894;font-weight:600}
.obv12-err-text{color:#e17055}
.obv12-muted{color:#666}
.obv12-file-info{font-size:11px;color:#8a8880;margin-top:4px}
.obv12-canales{display:flex;gap:18px;align-items:center;padding:4px 0}
.obv12-canales label{display:flex;align-items:center;gap:6px;cursor:pointer;font-size:13px}
.obv12-test-banner{background:#1a2a3d;border:1px solid #74b9ff;border-radius:8px;padding:10px 16px;color:#74b9ff;font-size:12px;font-weight:500;margin-top:16px;text-align:center}
.obv12-confirm-result{background:#1f3d2a;border:1px solid #00b894;border-radius:8px;padding:14px 18px;color:#00b894;font-weight:600;margin-top:12px;font-size:14px}
.obv12-hist-header{display:flex;align-items:center;justify-content:space-between;margin-bottom:12px}
.obv12-btn-sm{padding:6px 14px;background:#2a2a35;color:#8a8880;border:1px solid #3a3a45;border-radius:6px;font-size:11px;cursor:pointer;transition:background .2s}
.obv12-btn-sm:hover{background:#333;color:#ccc}
.obv12-btn-sm:disabled{cursor:wait;opacity:.6}
.obv12-btn-detail{padding:3px 10px;background:none;border:1px solid #444;border-radius:4px;color:#74b9ff;font-size:10px;cursor:pointer}
.obv12-btn-detail:hover{background:#1a2a3d}
.obv12-btn-back{padding:4px 12px;background:none;border:1px solid #444;border-radius:4px;color:#8a8880;font-size:11px;cursor:pointer;margin-bottom:10px}
.obv12-btn-back:hover{color:#ccc}
.obv12-detail-section{margin-top:12px}
.obv12-detail-title{font-size:12px;font-weight:600;color:#8a8880;text-transform:uppercase;margin-bottom:6px}
.obv12-estado-badge{display:inline-block;padding:2px 8px;border-radius:10px;font-size:10px;font-weight:600}
`;

// ═══════════════════════════════════════════════════════
// Componente principal
// ═══════════════════════════════════════════════════════

export default function OutboundV12Panel({ onSwitchToV1 }) {
  // Form state
  const [templateKey, setTemplateKey] = useState('');
  const [cicloKey, setCicloKey] = useState('');
  const [periodoMes, setPeriodoMes] = useState(new Date().getMonth() + 1);
  const [periodoAnio, setPeriodoAnio] = useState(new Date().getFullYear());
  const [channels, setChannels] = useState(['whatsapp']);
  const [archivo, setArchivo] = useState(null);
  const [manualValues, setManualValues] = useState({});

  // UI state — dry-run
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [resultado, setResultado] = useState(null);
  const [tab, setTab] = useState('checklist');

  // UI state — confirmar campaña
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmResult, setConfirmResult] = useState(null);
  const [confirmError, setConfirmError] = useState('');

  // UI state — historial campañas test
  const [campanas, setCampanas] = useState(null);
  const [campanasLoading, setCampanasLoading] = useState(false);
  const [detalle, setDetalle] = useState(null);
  const [detalleLoading, setDetalleLoading] = useState(false);
  const [sendTestLoading, setSendTestLoading] = useState(false);
  const [sendTestResult, setSendTestResult] = useState(null);
  const [actionMsg, setActionMsg] = useState('');

  // Derived
  const tplConfig = useMemo(() => TEMPLATES.find(t => t.key === templateKey), [templateKey]);
  const requiereCiclo = tplConfig?.requiereCiclo || false;
  const camposManuales = tplConfig?.camposManuales || [];

  const grupos = useMemo(() => {
    const g = {};
    TEMPLATES.forEach(t => { if (!g[t.grupo]) g[t.grupo] = []; g[t.grupo].push(t); });
    return g;
  }, []);

  // ── Handlers ──────────────────────────────────────────

  function toggleCanal(canal) {
    setChannels(prev => prev.includes(canal) ? prev.filter(c => c !== canal) : [...prev, canal]);
  }

  function handleManual(campo, valor) {
    setManualValues(prev => ({ ...prev, [campo]: valor }));
  }

  function resetResultado() {
    setResultado(null);
    setErrorMsg('');
    setTab('checklist');
    setConfirmResult(null);
    setConfirmError('');
  }

  async function procesarBase() {
    if (!templateKey) return setErrorMsg('Seleccioná un template');
    if (!archivo) return setErrorMsg('Subí un archivo CSV o XLSX');
    if (requiereCiclo && !cicloKey) return setErrorMsg('Seleccioná un ciclo para este template');
    if (channels.length === 0) return setErrorMsg('Seleccioná al menos un canal');

    setLoading(true);
    setErrorMsg('');
    setResultado(null);
    setConfirmResult(null);
    setConfirmError('');

    try {
      const formData = new FormData();
      formData.append('file', archivo);
      formData.append('templateKey', templateKey);
      if (cicloKey) formData.append('cicloKey', cicloKey);
      if (requiereCiclo) {
        formData.append('periodoMes', String(periodoMes));
        formData.append('periodoAnio', String(periodoAnio));
        formData.append('controlCompromisoMes', String(periodoMes));
        formData.append('controlCompromisoAnio', String(periodoAnio));
      }
      formData.append('channels', JSON.stringify(channels));

      const mv = {};
      camposManuales.forEach(c => { if (manualValues[c]) mv[c] = manualValues[c]; });
      if (Object.keys(mv).length > 0) formData.append('manualValues', JSON.stringify(mv));

      const endpoint = `${API_URL}/api/outbound/procesar-csv`;
      console.log('[V12] Procesando →', endpoint);
      const res = await authFetch(endpoint, { method: 'POST', body: formData });
      const data = await safeJson(res, 'procesar-csv');

      if (!res.ok && !data.ok) {
        setErrorMsg(data.mensaje || data.error || 'Error procesando archivo');
        return;
      }

      setResultado(data);
      setTab('checklist');
    } catch (err) {
      setErrorMsg('Error de conexión: ' + (err.message || err));
    } finally {
      setLoading(false);
    }
  }

  // ── Confirmar campaña (guarda en Supabase, NO envía) ──

  async function confirmarCampana() {
    if (!resultado || resultado.blocked) return;

    setConfirmLoading(true);
    setConfirmError('');
    setConfirmResult(null);

    try {
      const payload = {
        templateKey,
        cicloKey: cicloKey || null,
        periodoMes: requiereCiclo ? periodoMes : null,
        periodoAnio: requiereCiclo ? periodoAnio : null,
        channels,
        resumen: `${templateKey} | ${resultado.stats?.contactosAptos || 0} contacto(s) | ${(resultado.enviosPlanificados || []).length} envío(s)`,
        stats: resultado.stats,
        contactosReady: resultado.contactosReady,
        enviosPlanificados: resultado.enviosPlanificados,
        aptosAuditoria: resultado.aptosAuditoria,
        blocked: resultado.blocked
      };

      const endpoint = `${API_URL}/api/outbound/v12/confirmar-campana`;
      console.log('[V12] Confirmando campaña →', endpoint);
      const res = await authFetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await safeJson(res, 'confirmar-campana');

      if (!res.ok || !data.ok) {
        const msg = data.mensaje || data.error || 'Error confirmando campaña';
        if (data.rechazados && data.rechazados.length > 0) {
          const detalles = data.rechazados.map(r => `→ ${r.nombre}: ${r.motivo} (${r.telefono || r.email || '?'})`).join('\n');
          setConfirmError(`${msg}\n${detalles}`);
        } else {
          setConfirmError(msg);
        }
        return;
      }

      setConfirmResult(data);
      console.log('[V12] Campaña confirmada:', data);
    } catch (err) {
      setConfirmError('Error de conexión: ' + (err.message || err));
    } finally {
      setConfirmLoading(false);
    }
  }

  // ── Historial campañas ─────────────────────────────────

  async function cargarCampanas() {
    setCampanasLoading(true);
    try {
      const res = await authFetch(`${API_URL}/api/outbound/v12/campanas`);
      const data = await safeJson(res, 'campanas-list');
      if (data.ok) setCampanas(data.campanas);
    } catch (err) {
      console.error('[V12] Error cargando campañas:', err);
    } finally {
      setCampanasLoading(false);
    }
  }

  async function cargarDetalle(id) {
    setDetalleLoading(true);
    setDetalle(null);
    setSendTestResult(null);
    setActionMsg('');
    try {
      const res = await authFetch(`${API_URL}/api/outbound/v12/campanas/${id}`);
      const data = await safeJson(res, 'campana-detalle');
      if (data.ok) setDetalle(data);
    } catch (err) {
      console.error('[V12] Error cargando detalle:', err);
    } finally {
      setDetalleLoading(false);
    }
  }

  async function accionCampana(id, accion) {
    setActionMsg('');
    try {
      const res = await authFetch(`${API_URL}/api/outbound/v12/campanas/${id}/${accion}`, { method: 'POST', headers: { 'Content-Type': 'application/json' } });
      const data = await safeJson(res, 'accion-campana');
      setActionMsg(data.ok ? `✅ ${accion} OK` : `❌ ${data.mensaje || data.error}`);
      setTimeout(() => cargarDetalle(id), 300);
      setTimeout(() => cargarCampanas(), 500);
    } catch (err) {
      setActionMsg('❌ Error: ' + err.message);
    }
  }

  async function despacharCanal(id, canal) {
    const canalLabel = canal === 'email' ? 'Email' : 'WhatsApp';

    if (!window.confirm(`Vas a iniciar el envío por ${canalLabel}. El CRM procesará internamente en lotes seguros. ¿Confirmás?`)) return;

    setActionMsg(`⏳ Iniciando envío interno por ${canalLabel}...`);

    try {
      const res = await authFetch(`${API_URL}/api/outbound/v12/campanas/${id}/despachar-canal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ canal })
      });

      const data = await safeJson(res, 'despachar-canal');

      if (data.ok) {
        if (data.iniciado) {
          setActionMsg(`✅ ${canalLabel}: despacho iniciado. Pendientes iniciales: ${data.pendientes_iniciales || 0}. El CRM procesa internamente.`);
        } else {
          setActionMsg(`✅ ${canalLabel}: ${data.mensaje || 'sin pendientes'}`);
        }
      } else if (data.error === 'despacho_ya_activo') {
        setActionMsg(`⚠️ ${canalLabel}: ya hay un envío en curso para esta campaña/canal.`);
      } else if (data.error === 'estado_no_permite_despacho') {
        setActionMsg(`⚠️ ${canalLabel}: la campaña está en estado ${data.estado}.`);
      } else {
        setActionMsg(`❌ ${data.mensaje || data.error || 'Error iniciando despacho'}`);
      }

      setTimeout(() => cargarDetalle(id), 1000);
      setTimeout(() => cargarDetalle(id), 5000);
      setTimeout(() => cargarCampanas(), 1200);
      setTimeout(() => cargarCampanas(), 6000);

    } catch (err) {
      setActionMsg('❌ Error: ' + err.message);
    }
  }

  async function eliminarCampana(id) {
    if (!window.confirm('Vas a eliminar esta campaña y sus envíos/contactos asociados. ¿Confirmás?')) return;
    setActionMsg('⏳ Eliminando campaña...');
    try {
      const res = await authFetch(`${API_URL}/api/outbound/v12/campanas/${id}`, {
        method: 'DELETE', headers: { 'Content-Type': 'application/json' }
      });
      const data = await safeJson(res, 'eliminar-campana');
      if (data.ok) {
        setActionMsg(`✅ Campaña eliminada: ${data.eliminados.envios} envíos, ${data.eliminados.contactos} contactos borrados`);
        setDetalle(null);
        setTimeout(() => cargarCampanas(), 300);
      } else {
        setActionMsg(`❌ ${data.mensaje || data.error}`);
      }
    } catch (err) {
      setActionMsg('❌ Error: ' + err.message);
    }
  }

  async function enviarTest(campaniaId) {
    setSendTestLoading(true);
    setSendTestResult(null);
    try {
      const endpoint = `${API_URL}/api/outbound/v12/enviar-test/${campaniaId}`;
      console.log('[V12] Enviando test →', endpoint);
      const res = await authFetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await safeJson(res, 'enviar-test');
      setSendTestResult(data);
      console.log('[V12] Enviar test resultado:', data);
      // Refrescar detalle para ver estado actualizado
      if (data.ok || data.error) {
        setTimeout(() => cargarDetalle(campaniaId), 500);
      }
    } catch (err) {
      setSendTestResult({ ok: false, error: 'Error de conexión: ' + (err.message || err) });
    } finally {
      setSendTestLoading(false);
    }
  }

  // ── Render helpers ────────────────────────────────────

  function renderStats() {
    const s = resultado.stats;
    const items = [
      { label: 'Total', val: s.total, color: '#fff' },
      { label: 'Aptos', val: s.contactosAptos, color: '#00b894' },
      { label: 'Rechazados', val: s.rechazados, color: '#e17055' },
      { label: 'Revisión', val: s.requiereRevision, color: '#fdcb6e' },
      { label: 'Tel inv.', val: s.telefonosInvalidos, color: '#e17055' },
      { label: 'Email inv.', val: s.emailsInvalidos, color: '#e17055' },
      { label: 'WA', val: s.enviosWhatsApp, color: '#00b894' },
      { label: 'Email', val: s.enviosEmail, color: '#6c5ce7' },
      { label: 'SMS', val: s.enviosSms, color: '#fdcb6e' },
    ];
    return (
      <div className="obv12-stats">
        {items.map(i => (
          <div key={i.label} className="obv12-stat">
            <div className="obv12-stat-val" style={{ color: i.color }}>{i.val}</div>
            <div className="obv12-stat-label">{i.label}</div>
          </div>
        ))}
      </div>
    );
  }

  function renderChecklist() {
    return (
      <div className="obv12-table-wrap">
        <table className="obv12-table">
          <thead><tr><th className="obv12-th">Control</th><th className="obv12-th">Detalle</th><th className="obv12-th">Estado</th></tr></thead>
          <tbody>
            {(resultado.checklist || []).map((c, i) => (
              <tr key={i}>
                <td className="obv12-td">{c.control}</td>
                <td className="obv12-td" style={{ fontSize: '11px', color: '#999', maxWidth: 300, wordBreak: 'break-word' }}>{c.detalle}</td>
                <td className="obv12-td">
                  <span className="obv12-chk" style={{ background: c.estado === 'OK' ? '#0d3320' : c.estado === 'BLOQUEANTE' ? '#3d1f1f' : '#3d3520', color: c.estado === 'OK' ? '#00b894' : c.estado === 'BLOQUEANTE' ? '#e17055' : '#fdcb6e' }}>
                    {c.estado}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  function renderContactos() {
    const rows = resultado.contactosReady || [];
    if (!rows.length) return <div className="obv12-muted" style={{ padding: 20, textAlign: 'center' }}>Sin contactos aptos</div>;
    return (
      <div className="obv12-table-wrap">
        <table className="obv12-table">
          <thead><tr>
            <th className="obv12-th">#</th><th className="obv12-th">Cliente</th><th className="obv12-th">ID</th>
            <th className="obv12-th">Teléfono</th><th className="obv12-th">Email</th><th className="obv12-th">Estado</th>
            <th className="obv12-th">Deuda</th><th className="obv12-th">Canal</th>
          </tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td className="obv12-td obv12-muted">{i + 1}</td>
                <td className="obv12-td">{r.cliente_nombre}</td>
                <td className="obv12-td obv12-mono">{r.customer_id}</td>
                <td className="obv12-td obv12-mono">{r.telefono || '—'}</td>
                <td className="obv12-td" style={{ fontSize: 11 }}>{r.email || '—'}</td>
                <td className="obv12-td"><span className="obv12-badge" style={{ background: '#0d3320', color: '#00b894' }}>{r.estado_isp}</span></td>
                <td className="obv12-td" style={{ textAlign: 'right' }}>{r.deuda > 0 ? `$${Number(r.deuda).toLocaleString('es-AR')}` : '—'}</td>
                <td className="obv12-td">{(r.canales_json || []).join(', ')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  function renderRechazados() {
    const rows = resultado.rechazados || [];
    if (!rows.length) return <div className="obv12-muted" style={{ padding: 20, textAlign: 'center' }}>Sin rechazados</div>;
    return (
      <div className="obv12-table-wrap">
        <table className="obv12-table">
          <thead><tr>
            <th className="obv12-th">Fila</th><th className="obv12-th">Cliente</th><th className="obv12-th">ID</th>
            <th className="obv12-th">Estado</th><th className="obv12-th">Deuda</th><th className="obv12-th">Motivos</th>
          </tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td className="obv12-td obv12-muted">{r.fila}</td>
                <td className="obv12-td">{r.nombre}</td>
                <td className="obv12-td obv12-mono">{r.customer_id}</td>
                <td className="obv12-td">{r.estado || '—'}</td>
                <td className="obv12-td" style={{ textAlign: 'right' }}>{r.deuda > 0 ? `$${Number(r.deuda).toLocaleString('es-AR')}` : '—'}</td>
                <td className="obv12-td obv12-err-text" style={{ fontSize: 11, maxWidth: 300, wordBreak: 'break-word' }}>{r.motivos}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  function renderInvalidos() {
    const tels = resultado.telefonosInvalidos || [];
    const emails = resultado.emailsInvalidos || [];
    if (!tels.length && !emails.length) return <div className="obv12-muted" style={{ padding: 20, textAlign: 'center' }}>Sin inválidos</div>;
    return (
      <div>
        {tels.length > 0 && (<>
          <div className="obv12-label" style={{ margin: '4px 0 8px' }}>Teléfonos inválidos ({tels.length})</div>
          <div className="obv12-table-wrap" style={{ marginBottom: 14 }}>
            <table className="obv12-table">
              <thead><tr><th className="obv12-th">Fila</th><th className="obv12-th">Cliente</th><th className="obv12-th">ID</th><th className="obv12-th">Tel original</th><th className="obv12-th">Motivo</th></tr></thead>
              <tbody>{tels.map((r, i) => (
                <tr key={i}>
                  <td className="obv12-td obv12-muted">{r.fila}</td><td className="obv12-td">{r.nombre}</td>
                  <td className="obv12-td obv12-mono">{r.customer_id}</td>
                  <td className="obv12-td obv12-err-text">{r.telefono_original || '(vacío)'}</td>
                  <td className="obv12-td" style={{ fontSize: 11, color: '#999' }}>{r.motivo}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </>)}
        {emails.length > 0 && (<>
          <div className="obv12-label" style={{ margin: '0 0 8px' }}>Emails inválidos ({emails.length})</div>
          <div className="obv12-table-wrap">
            <table className="obv12-table">
              <thead><tr><th className="obv12-th">Fila</th><th className="obv12-th">Cliente</th><th className="obv12-th">ID</th><th className="obv12-th">Email original</th><th className="obv12-th">Motivo</th></tr></thead>
              <tbody>{emails.map((r, i) => (
                <tr key={i}>
                  <td className="obv12-td obv12-muted">{r.fila}</td><td className="obv12-td">{r.nombre}</td>
                  <td className="obv12-td obv12-mono">{r.customer_id}</td>
                  <td className="obv12-td obv12-err-text">{r.email_original || '(vacío)'}</td>
                  <td className="obv12-td" style={{ fontSize: 11, color: '#999' }}>{r.motivo}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </>)}
      </div>
    );
  }

  function renderEnvios() {
    const rows = resultado.enviosPlanificados || [];
    if (!rows.length) return <div className="obv12-muted" style={{ padding: 20, textAlign: 'center' }}>Sin envíos planificados</div>;
    const cc = { whatsapp: '#00b894', email: '#6c5ce7', sms: '#fdcb6e' };
    return (
      <div className="obv12-table-wrap">
        <table className="obv12-table">
          <thead><tr><th className="obv12-th">#</th><th className="obv12-th">Canal</th><th className="obv12-th">Destino</th><th className="obv12-th">Template</th><th className="obv12-th">Estado</th></tr></thead>
          <tbody>{rows.map((r, i) => (
            <tr key={i}>
              <td className="obv12-td obv12-muted">{i + 1}</td>
              <td className="obv12-td"><span className="obv12-badge" style={{ background: '#1a1a22', border: `1px solid ${cc[r.canal]||'#666'}`, color: cc[r.canal]||'#ccc' }}>{r.canal}</span></td>
              <td className="obv12-td obv12-mono">{r.destino}</td>
              <td className="obv12-td" style={{ fontSize: 11, color: '#999' }}>{r.template_name}</td>
              <td className="obv12-td"><span className="obv12-badge" style={{ background: '#1a2a3d', color: '#74b9ff' }}>{r.estado}</span></td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    );
  }

  function renderResumen() {
    return (
      <div className="obv12-table-wrap">
        <table className="obv12-table" style={{ minWidth: 400 }}>
          <thead><tr><th className="obv12-th">Campo</th><th className="obv12-th">Valor</th></tr></thead>
          <tbody>{(resultado.resumen || []).map((r, i) => (
            <tr key={i}>
              <td className="obv12-td" style={{ color: '#8a8880', fontWeight: 600 }}>{r.campo}</td>
              <td className="obv12-td">{String(r.valor)}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    );
  }

  // ── Render principal ──────────────────────────────────

  return (
    <>
      <style>{INJECTED_CSS}</style>
      <div className="marketing-page">
        <div className="obv12-panel">
        {onSwitchToV1 && (
          <button onClick={onSwitchToV1} style={{ background: 'none', border: '1px solid #333', borderRadius: '6px', color: '#8a8880', padding: '5px 12px', fontSize: '11px', cursor: 'pointer', marginBottom: '12px' }}>
            ← Outbound v1
          </button>
        )}
        <h1 className="obv12-h1">Outbound V12 — Auditoría de Campaña</h1>
        <p className="obv12-sub">Modo dry-run · Solo procesa y audita · No envía mensajes</p>

        {/* ── FORMULARIO ── */}
        <div className="obv12-card">
          {/* Fila 1: Template */}
          <div style={{ marginBottom: 14 }}>
            <label className="obv12-label">Template / Hito</label>
            <select className="obv12-select" value={templateKey} onChange={e => { const newKey = e.target.value; setTemplateKey(newKey); setCicloKey(''); const tpl = TEMPLATES.find(t => t.key === newKey); setManualValues(tpl && tpl.defaults ? { ...tpl.defaults } : {}); resetResultado(); }}>
              <option value="">— Seleccionar template —</option>
              {Object.entries(grupos).map(([grupo, tpls]) => (
                <optgroup key={grupo} label={grupo}>
                  {tpls.map(t => <option key={t.key} value={t.key}>{t.nombre}</option>)}
                </optgroup>
              ))}
            </select>
          </div>

          {/* Fila 2: Ciclo + Período (condicional) */}
          {requiereCiclo && (
            <div className="obv12-grid3">
              <div>
                <label className="obv12-label">Ciclo</label>
                <select className="obv12-select" value={cicloKey} onChange={e => { setCicloKey(e.target.value); resetResultado(); }}>
                  <option value="">— Seleccionar —</option>
                  {CICLOS.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label className="obv12-label">Mes período</label>
                <select className="obv12-select" value={periodoMes} onChange={e => { setPeriodoMes(Number(e.target.value)); resetResultado(); }}>
                  {MESES.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="obv12-label">Año</label>
                <select className="obv12-select" value={periodoAnio} onChange={e => { setPeriodoAnio(Number(e.target.value)); resetResultado(); }}>
                  {[2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
            </div>
          )}

          {/* Fila 3: Canales + Archivo */}
          <div className="obv12-grid">
            <div>
              <label className="obv12-label">Canales</label>
              <div className="obv12-canales">
                {CANALES.map(c => (
                  <label key={c.key}>
                    <input type="checkbox" checked={channels.includes(c.key)} onChange={() => { toggleCanal(c.key); resetResultado(); }} />
                    {c.label}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="obv12-label">Archivo CSV / XLSX</label>
              <input type="file" accept=".csv,.xlsx,.xls" className="obv12-file" onChange={e => { setArchivo(e.target.files?.[0] || null); resetResultado(); }} />
              {archivo && <div className="obv12-file-info">{archivo.name} ({(archivo.size / 1024).toFixed(1)} KB)</div>}
            </div>
          </div>

          {/* Fila 4: Campos manuales dinámicos */}
          {camposManuales.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <label className="obv12-label">Valores manuales</label>
              <div className="obv12-grid">
                {camposManuales.map(c => (
                  <div key={c}>
                    <input className="obv12-input" placeholder={CAMPO_LABELS[c] || c} value={manualValues[c] || ''} onChange={e => handleManual(c, e.target.value)} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Botón procesar */}
          <button
            className={loading ? 'obv12-btn-loading' : 'obv12-btn'}
            disabled={loading || !templateKey || !archivo}
            onClick={procesarBase}
          >
            {loading ? '⏳ Procesando...' : '🔍 Procesar base'}
          </button>
        </div>

        {/* ── ERROR ── */}
        {errorMsg && <div className="obv12-error">❌ {errorMsg}</div>}

        {/* ── WARNINGS ── */}
        {resultado?.warnings?.length > 0 && resultado.warnings.map((w, i) => (
          <div key={i} className="obv12-warning">⚠️ {w.mensaje || w.tipo}</div>
        ))}

        {/* ── RESULTADOS ── */}
        {resultado && (<>
          {resultado.blocked
            ? <div className="obv12-blocked">🚫 BLOQUEADO — La base no cumple las condiciones mínimas</div>
            : <div className="obv12-success">✅ Base procesada — {resultado.stats.contactosAptos} contacto(s) apto(s)</div>
          }

          {renderStats()}

          {resultado.archivo && (
            <div className="obv12-file-info" style={{ marginBottom: 14 }}>
              📄 {resultado.archivo.nombre} — {resultado.archivo.totalFilas} fila(s) — .{resultado.archivo.extension}
            </div>
          )}

          <div className="obv12-card">
            <div className="obv12-tabs">
              {TABS.map(t => {
                let count = '';
                if (t.key === 'aptos') count = ` (${resultado.stats?.contactosAptos || 0})`;
                if (t.key === 'rechazados') count = ` (${resultado.stats?.rechazados || 0})`;
                if (t.key === 'invalidos') count = ` (${(resultado.stats?.telefonosInvalidos || 0) + (resultado.stats?.emailsInvalidos || 0)})`;
                if (t.key === 'envios') count = ` (${(resultado.enviosPlanificados || []).length})`;
                return <button key={t.key} className={tab === t.key ? 'obv12-tab-active' : 'obv12-tab'} onClick={() => setTab(t.key)}>{t.label}{count}</button>;
              })}
            </div>
            {tab === 'checklist' && renderChecklist()}
            {tab === 'aptos' && renderContactos()}
            {tab === 'rechazados' && renderRechazados()}
            {tab === 'invalidos' && renderInvalidos()}
            {tab === 'envios' && renderEnvios()}
            {tab === 'resumen' && renderResumen()}
          </div>

          {/* ── BANNER MODO TEST ── */}
          <div className="obv12-test-banner">
            🧪 Modo auditoría — Solo procesa y confirma — No envía hasta ejecutar lote
          </div>

          {/* ── BOTÓN CONFIRMAR ── */}
          {!confirmResult && (
            <button
              className={confirmLoading ? 'obv12-btn-loading' : (resultado && !resultado.blocked ? 'obv12-btn-confirm-active' : 'obv12-btn-confirm')}
              disabled={confirmLoading || resultado.blocked}
              onClick={confirmarCampana}
            >
              {confirmLoading ? '⏳ Confirmando...' : '🧪 Confirmar campaña — no envía'}
            </button>
          )}

          {/* ── ERROR CONFIRMAR ── */}
          {confirmError && <div className="obv12-error" style={{ marginTop: 12 }}>❌ {confirmError}</div>}

          {/* ── RESULTADO CONFIRMAR ── */}
          {confirmResult && (
            <div className="obv12-confirm-result">
              ✅ Campaña guardada sin envío real
              <div style={{ marginTop: 8, fontSize: 13 }}>
                <strong>ID:</strong> {confirmResult.campaniaId} · <strong>Contactos:</strong> {confirmResult.contactosGuardados} · <strong>Envíos pendientes:</strong> {confirmResult.enviosPendientes}
              </div>
              <div style={{ marginTop: 6, fontSize: 11, color: '#8a8880' }}>
                modo: {confirmResult.modo} · tipo: {confirmResult.testMode ? 'prueba' : 'real'} · envío real: {String(confirmResult.realSendEnabled)} · worker: {String(confirmResult.workerEnabled)}
              </div>
            </div>
          )}
        </>)}

        {/* ── HISTORIAL CAMPAÑAS ── */}
        <div className="obv12-card" style={{ marginTop: 24 }}>
          <div className="obv12-hist-header">
            <span style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>📋 Campañas</span>
            <button className="obv12-btn-sm" disabled={campanasLoading} onClick={cargarCampanas}>
              {campanasLoading ? '⏳ ...' : '🔄 Actualizar'}
            </button>
          </div>

          {campanas === null && !campanasLoading && (
            <div className="obv12-muted" style={{ textAlign: 'center', padding: 16 }}>Presioná "Actualizar" para cargar</div>
          )}

          {campanas && campanas.length === 0 && (
            <div className="obv12-muted" style={{ textAlign: 'center', padding: 16 }}>Sin campañas</div>
          )}

          {/* Lista */}
          {campanas && campanas.length > 0 && !detalle && (
            <div className="obv12-table-wrap" style={{ maxHeight: 380 }}>
              <table className="obv12-table">
                <thead><tr>
                  <th className="obv12-th">ID</th><th className="obv12-th">Template</th>
                  <th className="obv12-th">Estado</th><th className="obv12-th">Progreso</th>
                  <th className="obv12-th">Tipo</th><th className="obv12-th">Fecha</th>
                  <th className="obv12-th"></th>
                </tr></thead>
                <tbody>
                  {campanas.map(c => {
                    const ec = { confirmada:'#6c5ce7', enviando:'#fdcb6e', pausada:'#e17055', finalizada:'#00b894', cancelada:'#666' };
                    const pct = c.total_envios > 0 ? Math.round((c.enviados / c.total_envios) * 100) : 0;
                    return (
                      <tr key={c.id}>
                        <td className="obv12-td obv12-mono">{c.id}</td>
                        <td className="obv12-td" style={{ fontSize: 11 }}>{c.template_name}</td>
                        <td className="obv12-td">
                          <span className="obv12-estado-badge" style={{ background:'#12121a', border:'1px solid '+(ec[c.estado]||'#666'), color:ec[c.estado]||'#ccc' }}>{c.estado}</span>
                        </td>
                        <td className="obv12-td" style={{ minWidth: 120 }}>
                          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                            <div style={{ flex:1, height:6, background:'#2a2a35', borderRadius:3, overflow:'hidden' }}>
                              <div style={{ width:pct+'%', height:'100%', background:ec[c.estado]||'#6c5ce7', borderRadius:3 }}></div>
                            </div>
                            <span style={{ fontSize:10, color:'#8a8880', whiteSpace:'nowrap' }}>{c.enviados}/{c.total_envios}{c.fallidos > 0 ? ' ❌'+c.fallidos : ''}</span>
                          </div>
                        </td>
                        <td className="obv12-td" style={{ fontSize:10, color:'#666' }}>{c.es_test ? '🧪' : '🔴'}</td>
                        <td className="obv12-td" style={{ fontSize:10, color:'#666' }}>{c.created_at ? new Date(c.created_at).toLocaleDateString('es-AR') : '—'}</td>
                        <td className="obv12-td"><button className="obv12-btn-detail" onClick={() => cargarDetalle(c.id)}>Ver</button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Detalle */}
          {detalleLoading && <div className="obv12-muted" style={{ textAlign:'center', padding:16 }}>⏳ Cargando...</div>}

          {detalle && (
            <div>
              <button className="obv12-btn-back" onClick={() => { setDetalle(null); setSendTestResult(null); setActionMsg(''); }}>← Volver</button>

              <div style={{ fontSize:14, fontWeight:600, color:'#fff', marginBottom:4 }}>
                Campaña #{detalle.campana.id} — {detalle.campana.template_name}
              </div>
              <div style={{ fontSize:11, color:'#8a8880', marginBottom:12 }}>
                estado: {detalle.campana.estado} · modo: {detalle.campana.modo} · enviados: {detalle.campana.enviados}/{detalle.campana.total_envios} · fallidos: {detalle.campana.fallidos} · {detalle.campana.es_test ? '🧪 prueba' : '🔴 real'}
              </div>

              {/* Progreso */}
              {detalle.campana.total_envios > 0 && (() => {
                const pct = Math.round((detalle.campana.enviados / detalle.campana.total_envios) * 100);
                return (
                  <div style={{ marginBottom:14 }}>
                    <div style={{ height:8, background:'#2a2a35', borderRadius:4, overflow:'hidden' }}>
                      <div style={{ width:pct+'%', height:'100%', background:'#6c5ce7', borderRadius:4, transition:'width .3s' }}></div>
                    </div>
                    <div style={{ fontSize:10, color:'#8a8880', marginTop:3 }}>{pct}% completado</div>
                  </div>
                );
              })()}

              {/* Acciones */}
              <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:12 }}>
                {(detalle.campana.estado === 'confirmada' || detalle.campana.estado === 'enviando') && (
                  <button className="obv12-btn-sm" onClick={() => accionCampana(detalle.campana.id, 'pausar')}>⏸ Pausar</button>
                )}
                {detalle.campana.estado === 'pausada' && (
                  <button className="obv12-btn-sm" onClick={() => accionCampana(detalle.campana.id, 'reanudar')}>▶ Reanudar</button>
                )}
                {!['cancelada','finalizada'].includes(detalle.campana.estado) && (
                  <button className="obv12-btn-sm" style={{ borderColor:'#e17055', color:'#e17055' }} onClick={() => { if(window.confirm('¿Cancelar campaña '+detalle.campana.id+'?')) accionCampana(detalle.campana.id, 'cancelar'); }}>✖ Cancelar</button>
                )}
                {detalle.envios.some(e => e.estado === 'fallido') && (
                  <button className="obv12-btn-sm" style={{ borderColor:'#fdcb6e', color:'#fdcb6e' }} onClick={() => accionCampana(detalle.campana.id, 'reintentar-fallidos')}>🔄 Reintentar fallidos</button>
                )}
                {detalle.envios.some(e => e.estado === 'pendiente') && !['cancelada','pausada'].includes(detalle.campana.estado) && (
                  <>
                    {detalle.envios.some(e => e.estado === 'pendiente' && e.canal === 'email') && (
                      <button className="obv12-btn-sm" style={{ borderColor:'#e17055', color:'#e17055' }} onClick={() => despacharCanal(detalle.campana.id, 'email')}>Enviar Email</button>
                    )}
                    {detalle.envios.some(e => e.estado === 'pendiente' && e.canal === 'whatsapp') && (
                      <button className="obv12-btn-sm" style={{ borderColor:'#6c5ce7', color:'#6c5ce7' }} onClick={() => despacharCanal(detalle.campana.id, 'whatsapp')}>Enviar WhatsApp</button>
                    )}
                  </>
                )}
                <button className="obv12-btn-sm" style={{ borderColor:'#636e72', color:'#636e72' }} onClick={() => eliminarCampana(detalle.campana.id)}>🗑️ Eliminar</button>
              </div>

              {/* Mensajes acción */}
              {actionMsg && <div style={{ fontSize:12, padding:'8px 12px', background:actionMsg.startsWith('✅')?'#1f3d2a':actionMsg.startsWith('⏳')?'#1a2a3d':'#3d1f1f', border:'1px solid '+(actionMsg.startsWith('✅')?'#00b894':actionMsg.startsWith('⏳')?'#74b9ff':'#e17055'), borderRadius:6, color:actionMsg.startsWith('✅')?'#00b894':actionMsg.startsWith('⏳')?'#74b9ff':'#e17055', marginBottom:12 }}>{actionMsg}</div>}

              {sendTestResult && sendTestResult.ok && (
                <div className="obv12-confirm-result" style={{ marginTop:0, marginBottom:12 }}>
                  ✅ Envío #{sendTestResult.envioId} · {sendTestResult.telefono} · {sendTestResult.template}
                  <div style={{ fontSize:11, color:'#8a8880', marginTop:4 }}>msgId: {sendTestResult.messageId || '—'} · vars: {JSON.stringify(sendTestResult.variables || [])}</div>
                </div>
              )}
              {sendTestResult && !sendTestResult.ok && (
                <div className="obv12-error" style={{ marginBottom:12 }}>❌ {sendTestResult.mensaje || sendTestResult.error}</div>
              )}

              {/* Contactos */}
              <div className="obv12-detail-section">
                <div className="obv12-detail-title">Contactos ({detalle.contactos.length})</div>
                {detalle.contactos.length > 0 ? (
                  <div className="obv12-table-wrap" style={{ maxHeight:200 }}>
                    <table className="obv12-table">
                      <thead><tr><th className="obv12-th">Nombre</th><th className="obv12-th">Teléfono</th><th className="obv12-th">Estado</th><th className="obv12-th">Metadata</th></tr></thead>
                      <tbody>{detalle.contactos.map(ct => (
                        <tr key={ct.id}>
                          <td className="obv12-td">{ct.nombre || '—'}</td>
                          <td className="obv12-td obv12-mono">{ct.telefono || '—'}</td>
                          <td className="obv12-td">{ct.estado_contacto}</td>
                          <td className="obv12-td" style={{ fontSize:10, color:'#666', maxWidth:200, wordBreak:'break-all' }}>{ct.metadata ? JSON.stringify(ct.metadata) : '—'}</td>
                        </tr>
                      ))}</tbody>
                    </table>
                  </div>
                ) : <div className="obv12-muted" style={{ padding:8 }}>Sin contactos</div>}
              </div>

              {/* Envíos */}
              <div className="obv12-detail-section">
                <div className="obv12-detail-title">Envíos ({detalle.envios.length})</div>
                {detalle.envios.length > 0 ? (
                  <div className="obv12-table-wrap" style={{ maxHeight:250 }}>
                    <table className="obv12-table">
                      <thead><tr><th className="obv12-th">ID</th><th className="obv12-th">Canal</th><th className="obv12-th">Teléfono</th><th className="obv12-th">Estado</th><th className="obv12-th">Reint.</th><th className="obv12-th">Enviado</th><th className="obv12-th">Error/MsgID</th></tr></thead>
                      <tbody>{detalle.envios.map(ev => {
                        const ec = { enviado:'#00b894', fallido:'#e17055', pendiente:'#74b9ff', procesando:'#fdcb6e', cancelado:'#666' };
                        return (
                          <tr key={ev.id}>
                            <td className="obv12-td obv12-mono">{ev.id}</td>
                            <td className="obv12-td" style={{ fontSize:10 }}>{ev.canal || 'wa'}</td>
                            <td className="obv12-td obv12-mono">{ev.telefono}</td>
                            <td className="obv12-td"><span className="obv12-estado-badge" style={{ background:'#12121a', border:'1px solid '+(ec[ev.estado]||'#666'), color:ec[ev.estado]||'#ccc' }}>{ev.estado}</span></td>
                            <td className="obv12-td" style={{ textAlign:'center', fontSize:10 }}>{ev.reintentos || 0}</td>
                            <td className="obv12-td" style={{ fontSize:10, color:'#666' }}>{ev.enviado_en ? new Date(ev.enviado_en).toLocaleString('es-AR') : '—'}</td>
                            <td className="obv12-td" style={{ fontSize:10, color:'#999', maxWidth:180, wordBreak:'break-all' }}>{ev.error_mensaje || '—'}</td>
                          </tr>
                        );
                      })}</tbody>
                    </table>
                  </div>
                ) : <div className="obv12-muted" style={{ padding:8 }}>Sin envíos</div>}
              </div>
            </div>
          )}
        </div>

      </div>
      </div>
    </>
  );
}
